import React, { useState, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TopTabbar from "../components/partials/TopTabbar";
import Header from "../components/partials/Header";
import Footer from "../components/partials/Footer";
import AccountDeletionService from '../services/AccountDeletionService';
import LoadingSpinner from '../utils/LoadingSpinner';
import AccountDeletionDetailModal from '../components/modals/accountDeletion/AccountDeletionDetailModal';

const AccountDeletionRequestsPage = () => {
    const [requests, setRequests] = useState([]);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [isPageLoading, setIsPageLoading] = useState(true);

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        try {
            const language = localStorage.getItem('language') || 'fr';
            const data = await AccountDeletionService.findAll(language);
            const sortedData = [...data].sort((a, b) => {
                return new Date(b.createdAt) - new Date(a.createdAt);
            });
            setRequests(sortedData);
        } catch (error) {
            console.error('Error fetching account deletion requests:', error);
            toast.error('Erreur lors de la récupération des demandes de suppression');
        } finally {
            setIsPageLoading(false);
        }
    };

    const formatStatus = (status) => {
        switch(status) {
            case 'TOKEN_VALIDATED':
                return 'En attente de validation admin';
            case 'APPROVED':
                return 'Approuvé';
            case 'REJECTED':
                return 'Rejeté';
            case 'PROCESSED':
                return 'Compte supprimé';
            case 'PENDING_TOKEN':
                return 'En attente de validation utilisateur';
            default:
                return status;
        }
    };

    const getStatusBadge = (status) => {
        switch(status) {
            case 'TOKEN_VALIDATED':
                return <span className="badge bg-info text-dark">En attente de validation admin</span>;
            case 'APPROVED':
                return <span className="badge bg-success">Approuvé</span>;
            case 'REJECTED':
                return <span className="badge bg-danger">Rejeté</span>;
            case 'PROCESSED':
                return <span className="badge bg-secondary">Compte supprimé</span>;
            case 'PENDING_TOKEN':
                return <span className="badge bg-warning text-dark">En attente de validation utilisateur</span>;
            default:
                return <span className="badge bg-secondary">{status}</span>;
        }
    };

    const handleApprove = async (request) => {
        if (request.status !== 'TOKEN_VALIDATED') {
            Swal.fire({
                icon: 'warning',
                title: 'Action impossible',
                text: 'Cette demande ne peut pas être approuvée car le token n\'a pas encore été validé par l\'utilisateur.',
                confirmButtonColor: '#24afcc'
            });
            return;
        }

        Swal.fire({
            title: 'Approuver la demande',
            html: `Voulez-vous vraiment approuver la suppression du compte de <strong>${request.username}</strong> ?<br/><br/>
                   <span class="text-danger">Cette action est irréversible et supprimera définitivement toutes les données de l'utilisateur.</span>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Oui, approuver et supprimer le compte',
            cancelButtonText: 'Annuler'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const language = localStorage.getItem('language') || 'fr';
                    await AccountDeletionService.approve(request.id, language);
                    toast.success(`La demande de suppression de ${request.username} a été approuvée et le compte a été supprimé avec succès`);
                    fetchRequests();
                    setShowDetailModal(false);
                } catch (error) {
                    console.error('Error approving request:', error);
                    toast.error('Erreur lors de l\'approbation de la demande');
                }
            }
        });
    };

    // Version corrigée - Plus de Swal.fire ici, on reçoit directement la raison
    const handleReject = async (request, reason) => {
        try {
            const language = localStorage.getItem('language') || 'fr';
            await AccountDeletionService.reject(request.id, reason, language);
            toast.info(`La demande de suppression de ${request.username} a été rejetée`);
            fetchRequests();
            setShowDetailModal(false);
        } catch (error) {
            console.error('Error rejecting request:', error);
            toast.error('Erreur lors du rejet de la demande');
        }
    };

    const handleReapprove = async (request) => {
        Swal.fire({
            title: 'Réapprouver la demande',
            text: `Voulez-vous vraiment réapprouver la suppression du compte de ${request.username} ? Cette action était précédemment rejetée.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Oui, réapprouver',
            cancelButtonText: 'Annuler'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const language = localStorage.getItem('language') || 'fr';
                    await AccountDeletionService.approve(request.id, language);
                    toast.success(`La demande de suppression de ${request.username} a été réapprouvée avec succès`);
                    fetchRequests();
                    setShowDetailModal(false);
                } catch (error) {
                    console.error('Error reapproving request:', error);
                    toast.error('Erreur lors de la réapprobation de la demande');
                }
            }
        });
    };

    const openRequestDetail = (request) => {
        setSelectedRequest(request);
        setShowDetailModal(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Date inconnue';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Date invalide';
            return date.toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return 'Date invalide';
        }
    };

    const filteredRequests = requests.filter(request => {
        const matchesSearch = request.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.user?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.user?.lastName?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus ? request.status === filterStatus : true;
        return matchesSearch && matchesStatus;
    });

    if (isPageLoading) {
        return <LoadingSpinner isVisible={true} loadingText="Chargement des demandes de suppression..." />;
    }

    return (
        <>
            <div className="d-flex flex-column vh-100">
                <div className="flex-grow-1 overflow-y-scroll">
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        maxWidth: '800px',
                        margin: '0 auto'
                    }}>
                        <div className="verify-number-main">
                            <TopTabbar />
                            <Header />
                            <div className="verify-number-top">
                                <div className="container">
                                    <div className="verify-number-top-content d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            <div className="header-title mt-32">
                                                <p style={{ fontSize: '25px' }}>Gestion des demandes de suppression</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="contact-search">
                                        <div className="input-group contact-searchbar">
                                            <div className="search-icon">
                                                <img src="assets/svg/search-icon.svg" alt="search-icon" />
                                            </div>
                                            <div className="seach-bar">
                                                <input
                                                    type="search"
                                                    placeholder="Rechercher par nom d'utilisateur, email ou nom complet"
                                                    className="form-control search-text"
                                                    id="search-input"
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="verify-number-bottom" id="send-money-contact">
                                <div className="container mb-35">
                                    <div className="nav nav-tabs custom-tab-contact" id="nav-tab" role="tablist" style={{ position: 'relative', marginBottom: '20px' }}>
                                        <button
                                            className={`nav-link ${!filterStatus ? 'active' : ''}`}
                                            id="nav-all-tab"
                                            data-bs-toggle="tab"
                                            data-bs-target="#nav-all"
                                            type="button"
                                            role="tab"
                                            aria-selected="true"
                                            onClick={() => setFilterStatus('')}
                                        >
                                            Toutes les demandes
                                        </button>
                                        <button
                                            className={`nav-link ${filterStatus === 'TOKEN_VALIDATED' ? 'active' : ''}`}
                                            id="nav-pending-tab"
                                            data-bs-toggle="tab"
                                            data-bs-target="#nav-pending"
                                            type="button"
                                            role="tab"
                                            aria-selected="false"
                                            onClick={() => setFilterStatus('TOKEN_VALIDATED')}
                                        >
                                            À valider
                                        </button>
                                        <Dropdown className="text-center" style={{ position: 'absolute', right: 0 }}>
                                            <Dropdown.Toggle
                                                variant="link"
                                                className="nav-link dropdown-toggle"
                                            >
                                                <i className="fas fa-filter me-2"></i>
                                                Filtrer ▼
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => setFilterStatus('')}>
                                                    Toutes les demandes
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => setFilterStatus('TOKEN_VALIDATED')}>
                                                    En attente de validation admin
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => setFilterStatus('APPROVED')}>
                                                    Approuvées
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => setFilterStatus('REJECTED')}>
                                                    Rejetées
                                                </Dropdown.Item>
                                                <Dropdown.Item onClick={() => setFilterStatus('PROCESSED')}>
                                                    Comptes supprimés
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>
                                </div>
                                <div className="verify-number-bottom-wrap">
                                    <div className="send-money-contact">
                                        {filteredRequests.map(request => (
                                            <div
                                                key={request.id}
                                                className="send-money-contact-tab request-money1 p-1 mb-2"
                                                onClick={() => openRequestDetail(request)}
                                                style={{ borderBottom: '3px solid #5a5c5e', cursor: 'pointer' }}
                                            >
                                                <div className="contact-profile">
                                                    <div style={{
                                                        height: "80px",
                                                        width: "80px",
                                                        borderRadius: "50%",
                                                        backgroundColor: "#e9ecef",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: "32px",
                                                        fontWeight: "bold",
                                                        color: "#24afcc"
                                                    }}>
                                                        {request.username?.charAt(0).toUpperCase()}
                                                    </div>
                                                </div>
                                                <div className="contact-details" style={{ flex: 1, minWidth: 0 }}>
                                                    <h3 style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {request.user?.firstName} {request.user?.lastName}
                                                    </h3>
                                                    <h4 style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        @{request.username}
                                                    </h4>
                                                    <small className="text-muted d-block" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {request.email}
                                                    </small>
                                                    <div className="d-flex align-items-center mt-1">
                                                        {getStatusBadge(request.status)}
                                                        <small className="text-muted ms-2">
                                                            {formatDate(request.createdAt)}
                                                        </small>
                                                    </div>
                                                    <small className="text-muted d-block mt-1" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        Raison: {request.deletionReason}
                                                    </small>
                                                </div>
                                            </div>
                                        ))}
                                        {filteredRequests.length === 0 && (
                                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                                <img src="assets/images/setting/no_data.png" alt="Aucune demande"
                                                     style={{ width: '100px', marginBottom: '20px' }} />
                                                <p style={{ color: '#666', fontSize: '1.2em' }}>Aucune demande de suppression trouvée</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <AccountDeletionDetailModal
                                show={showDetailModal}
                                request={selectedRequest}
                                onHide={() => setShowDetailModal(false)}
                                onApprove={handleApprove}
                                onReject={handleReject}
                                onReapprove={handleReapprove}
                                onRefresh={fetchRequests}
                                formatDate={formatDate}
                                formatStatus={formatStatus}
                            />
                            <Footer />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AccountDeletionRequestsPage;